import { KeyRound, RefreshCw } from 'lucide-react'
import { formatDateTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { AdminTrip } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { Badge } from '@/components/ui/StatusBadge'
import { sendTripToDriverApp } from './api'
import { driverAppVerdict, LIVE_STATUSES, OTS_STATUS_LABELS } from './driverApp'
import styles from './DriverAppPanel.module.css'

interface DriverAppPanelProps {
  trip: AdminTrip
  disabled?: boolean
}

/**
 * The trip's copy on the driver's Option Travel app, inside the trip drawer.
 *
 * Drive For Me drivers work their trips on the OTS driver app: the trip is
 * created there when a driver is assigned, and every change after is sent
 * again. This says whether the latest version arrived, why not if it did not,
 * and offers to send it again — the operator's answer to a driver on the phone
 * saying "I can't see the trip".
 */
export function DriverAppPanel({ trip, disabled = false }: DriverAppPanelProps) {
  const sync = trip.driverApp
  const verdict = driverAppVerdict(sync)
  const closed = trip.status === 'COMPLETED' || trip.status === 'CANCELLED'

  const sendMutation = useAdminMutation({
    mutationFn: (tripId: number) => sendTripToDriverApp(tripId),
    successTitle: 'Sent to the driver app',
    successDescription: (status) => {
      const after = driverAppVerdict(status)
      if (status?.state === 'FAILED' && status.lastError) return `${after.label}: ${status.lastError}`
      return after.label === 'On the driver app' ? 'The driver app has the latest version.' : after.label
    },
    invalidate: [queryKeys.trips.all],
    errorTitle: 'Could not send the trip to the driver app',
  })

  // A trip that never went, and has no reason to: nothing to say about it.
  if (!sync && !(trip.driverId && trip.status && LIVE_STATUSES.has(trip.status))) {
    return <p className={styles.muted}>This trip was not worked on the driver app.</p>
  }

  // Re-sending a finished trip only matters when its last word never arrived.
  const canSend = !closed || sync?.state === 'FAILED'

  return (
    <div className={styles.panel}>
      <div className={styles.headline}>
        <Badge tone={verdict.tone} dot>
          {verdict.label}
        </Badge>
        {sync?.otsTripId ? <span className={styles.muted}>OTS trip #{sync.otsTripId}</span> : null}
      </div>

      {trip.startCodeLocked ? (
        <p className={styles.alert} role="alert">
          <KeyRound aria-hidden="true" />
          <span>
            The driver entered a wrong start code too many times, so the driver app will not start this trip. Confirm
            with the customer that the driver is with them, then use <strong>Start trip</strong> above.
          </span>
        </p>
      ) : null}

      {sync ? (
        <KeyValueGrid>
          <KeyValue label="On the driver app as">
            {sync.otsStatus != null ? (OTS_STATUS_LABELS[sync.otsStatus] ?? `Status ${sync.otsStatus}`) : '—'}
          </KeyValue>
          <KeyValue label="Last delivered">{sync.syncedAt ? formatDateTime(sync.syncedAt) : 'Never'}</KeyValue>
          {sync.state !== 'SYNCED' || sync.outstanding ? (
            <>
              <KeyValue label="Attempts">{sync.attempts}</KeyValue>
              <KeyValue label="Next attempt">
                {sync.state === 'FAILED'
                  ? 'None — stopped retrying'
                  : sync.nextAttemptAt
                    ? formatDateTime(sync.nextAttemptAt)
                    : 'As soon as possible'}
              </KeyValue>
            </>
          ) : null}
          {sync.lastError ? (
            <KeyValue label="Last error" wide>
              <span className={styles.error}>{sync.lastError}</span>
            </KeyValue>
          ) : null}
        </KeyValueGrid>
      ) : (
        <p className={styles.muted}>
          The driver cannot see this trip on the driver app. Trips go there when a driver is assigned while the
          integration is on; send it now if it was assigned before.
        </p>
      )}

      {canSend ? (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<RefreshCw aria-hidden="true" />}
            loading={sendMutation.isPending}
            disabled={disabled || sendMutation.isPending}
            onClick={() => sendMutation.mutate(trip.id)}
          >
            {sync ? 'Send again' : 'Send to driver app'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
